<template>
  <section class="mt-6 flex flex-col gap-y-2" data-testid="experience-event-option-section">
    <ul class="flex flex-col gap-y-4">
      <li v-for="(experienceOption, index) in props.experienceOptions" :key="experienceOption.id + index" class="flex flex-col gap-y-1">
        <ExperienceEventOptionCard
          :count="calcCount(experienceOption.id)"
          :disabled="props.disabled"
          :experience-option="experienceOption"
          :is-public-price="props.isPublicPrice"
          @decrement="() => onClickDecrementButton(experienceOption)"
          @increment="() => onClickIncrementButton(experienceOption)"
        />
      </li>
    </ul>
  </section>
</template>

<script lang="ts" setup>
import type { WabunkaExperienceEventOptionViewModel } from '@/models/wabunka/wabunkaExperienceEventOption';
import type { WabunkaExperienceOptionViewModel } from '@/models/wabunka/wabunkaExperienceOption';
import type { WabunkaReservationExperienceEventOptionForm } from '@/wabunka-src/features/Experience';
import { ExperienceEventOptionCard } from '@/wabunka-src/features/Experience';

interface Props {
  disabled?: boolean;
  // 初期描画時のオプション選択肢フォーム（戻るボタンで戻ってきたときにセットする用）
  defaultOptionsFormList: WabunkaReservationExperienceEventOptionForm[];
  // 選択可能な体験イベントオプションのリスト
  experienceOptions: WabunkaExperienceEventOptionViewModel[] | WabunkaExperienceOptionViewModel[];
  // 購入予定のオプションに変更が加わった場合のイベントハンドラ
  onChange: (choices: WabunkaReservationExperienceEventOptionForm[]) => void;
  // 価格を表示するか否か
  isPublicPrice: boolean;
}

const props = defineProps<Props>();

// 現在の選択肢の個数を格納する computed リスト
// 個数0のものは冗長なので配列には格納しない
const refChoiceObjList = ref<WabunkaReservationExperienceEventOptionForm[]>([]);

// 引数で受け取った選択肢 ID の、現在の選択個数を算出して返却する
const calcCount = (id: string): number => {
  return refChoiceObjList.value.find((_) => _.experienceEventOption.id === id)?.count || 0;
};

// プラスボタンを押したときのイベントハンドラ
const onClickIncrementButton = (experienceEventOption: WabunkaExperienceEventOptionViewModel | WabunkaExperienceOptionViewModel) => {
  const oldCount = calcCount(experienceEventOption.id);
  const newCount = oldCount + 1;

  // 該当のオブジェクトを削除
  refChoiceObjList.value = refChoiceObjList.value.filter((_) => _.experienceEventOption.id !== experienceEventOption.id);

  // 詰め直し
  refChoiceObjList.value = refChoiceObjList.value.concat({ experienceEventOption: experienceEventOption, count: newCount });

  props.onChange(refChoiceObjList.value);
};

// マイナスボタンを押したときのイベントハンドラ
// 0個以下にはならないようにする
const onClickDecrementButton = (experienceEventOption: WabunkaExperienceEventOptionViewModel | WabunkaExperienceOptionViewModel) => {
  const oldCount = calcCount(experienceEventOption.id);
  const newCount = oldCount - 1;

  // 該当のオブジェクトを削除
  refChoiceObjList.value = refChoiceObjList.value.filter((_) => _.experienceEventOption.id !== experienceEventOption.id);

  // newCount が1より大きい数値だったら再度詰め直す
  if (newCount > 0) {
    refChoiceObjList.value = refChoiceObjList.value.concat({ experienceEventOption: experienceEventOption, count: newCount });
  }

  props.onChange(refChoiceObjList.value);
};

// 初期値のセット
const setDefaultValue = () => {
  refChoiceObjList.value = props.defaultOptionsFormList;
};
watch(() => props.defaultOptionsFormList, setDefaultValue, { immediate: true, deep: true });
</script>
